import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiRefreshCw, FiUsers } from 'react-icons/fi';
import { filterDataService } from '../../services/api';
import Loading from '../common/Loading';

function Vendedores() {
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [page, setPage] = useState(1);
  const perPage = 50;
  const [sortField, setSortField] = useState('nome');
  const [sortOrder, setSortOrder] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <span style={{ marginLeft: 4, opacity: 0.2, fontSize: 10, userSelect: 'none' }}>▲▼</span>;
    return <span style={{ marginLeft: 4, fontSize: 10, userSelect: 'none' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  const load = useCallback(async (pageNum = 1, searchTerm = search) => {
    setLoading(true);
    setPage(pageNum);
    try {
      const offset = (pageNum - 1) * perPage;
      const res = await filterDataService.getVendedores(searchTerm.trim(), perPage, offset, sortField, sortOrder);
      const list = res?.data?.vendedores || [];
      const pag = res?.data?.pagination;
      setRows(list);
      if (pag) setPagination(pag);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, sortField, sortOrder]);

  const totalPages = Math.ceil(pagination.total / perPage) || 1;

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    const offset = 0;
    clearTimeout(window._vendedoresSearchTimer);
    window._vendedoresSearchTimer = setTimeout(() => {
      filterDataService.getVendedores(value.trim(), perPage, offset, sortField, sortOrder).then(res => {
        setRows(res?.data?.vendedores || []);
        if (res?.data?.pagination) setPagination(res.data.pagination);
      }).catch(() => setRows([]));
    }, 300);
  };

  const handlePageChange = (newPage) => {
    load(newPage);
  };

  useEffect(() => {
    load(1);
  }, [load]);

  return (
    <div className="container-fluid py-4">
      <div className="mb-4">
        <h2 className="mb-1">Vendedores</h2>
        <p className="text-muted mb-0">
          Dados de vendedores
        </p>
      </div>

      <div className="settings-container">
        <div className="settings-card-wrapper">
          <div className="card-modern">
            <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '48px', height: '48px' }}
                  >
                    <FiUsers size={24} />
                  </div>
                  <div>
                    <h5 className="mb-0 fw-bold text-dark">Lista de vendedores</h5>
                    <p className="mb-0 text-muted small">Busca por nome ou código RCA</p>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-end">
                  <div className="form-group-modern mb-0" style={{ minWidth: '260px' }}>
                    <label>
                      <FiSearch className="me-1" size={14} />
                      Buscar
                    </label>
                    <input
                      type="search"
                      className="form-control-modern"
                      placeholder="Nome ou código…"
                      value={search}
                      onChange={(e) => handleSearchChange(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn-modern btn-modern-light"
                    onClick={() => load(page)}
                    disabled={loading}
                    title="Atualizar"
                  >
                    <FiRefreshCw size={16} className={loading ? 'spin-ico' : ''} />
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body-modern p-4 pt-0" style={{ position: 'relative' }}>
              {loading && rows.length > 0 && (
                <Loading.Overlay text="Atualizando vendedores..." size="sm" />
              )}
              {loading && rows.length === 0 ? (
                <Loading
                  text="Carregando vendedores..."
                  subtext="Sincronizando vendedores e supervisores do ERP..."
                />
              ) : rows.length === 0 ? (
                <div className="text-center py-5 text-muted small">Nenhum vendedor encontrado.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table-modern w-100">
                    <thead>
                      <tr>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSort('id')}>ID{renderSortIcon('id')}</th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSort('codusur')}>RCA{renderSortIcon('codusur')}</th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSort('nome')}>Nome{renderSortIcon('nome')}</th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSort('supervisor')}>Supervisor{renderSortIcon('supervisor')}</th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSort('codfilial')}>Filial{renderSortIcon('codfilial')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((v) => (
                        <tr key={v.id}>
                          <td className="text-muted small">{v.id}</td>
                          <td className="fw-semibold">{v.codigo}</td>
                          <td>{v.nome}</td>
                          <td>{v.supervisor || '—'}</td>
                          <td>{v.codfilial ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!loading && rows.length === 0 && (
                <div className="text-center py-5 text-muted small">Nenhum vendedor encontrado.</div>
              )}
              {pagination.total > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                  <small className="text-muted">
                    Mostrando {((page - 1) * perPage) + 1}-{Math.min(page * perPage, pagination.total)} de {pagination.total} vendedores
                  </small>
                  <div className="d-flex gap-1 align-items-center">
                    <button type="button" className="btn-modern btn-modern-light btn-modern-sm" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>Anterior</button>
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 7) pageNum = i + 1;
                      else if (page <= 4) pageNum = i + 1;
                      else if (page >= totalPages - 3) pageNum = totalPages - 6 + i;
                      else pageNum = page - 3 + i;
                      return (
                        <button key={pageNum} type="button" className={`btn-modern btn-modern-sm ${pageNum === page ? 'btn-modern-primary' : 'btn-modern-light'}`} onClick={() => handlePageChange(pageNum)} style={{ minWidth: '36px' }}>{pageNum}</button>
                      );
                    })}
                    <button type="button" className="btn-modern btn-modern-light btn-modern-sm" disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>Próximo</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .spin-ico { animation: spin-ico 0.8s linear infinite; display: inline-block; }
        @keyframes spin-ico { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default Vendedores;
